
import { useContext, useState } from "react"

import { 
  CustomSwitch, 
  Divider, 
  OpenToParentsModal, 
  OpenToStaffModal,
  ReadOnlyBadge 
} from "@components"

import { NotificationContext, UserContext } from "@context"
import { GET_STAFF_WITHOUT_SB_ACCOUNTS, GET_STUDENTS_WITHOUT_GUARDIANS } from "@graphql"
import { hasExpired, mySqlToLocal } from "@helpers"
import { useUpdateEvent } from "@hooks"
import { useApolloClient, useLazyQuery } from "@apollo/client";
import { Button } from "@heroui/react"

import PropTypes from "prop-types"

const EventAccess = ({ allowManualSbAccountMatching = false, backButton, event }) => {
  const initialFormData = {
    openStaff: event.open_staff,
    openParents: event.open_parents,
    disableParentBookings: event.disable_parent_bookings,
  }

  /**
   * State
   */
  const [form, setForm] = useState({ ...initialFormData })
  const [savedFormData, setSavedFormData] = useState({ ...initialFormData })
  const [showOpenToParentsModal, setShowOpenToParentsModal] = useState(false)
  const [showOpenToStaffModal, setShowOpenToStaffModal] = useState(false)

  /**
   * Context
   */
  const { notify } = useContext(NotificationContext)
  const { setCurrentEvent } = useContext(UserContext)

  /**
   * Hooks
   */
  const client = useApolloClient();
  const {updateEvent, loading: savingEvent} = useUpdateEvent()

  /**
   * GraphQL Queries
   */
  const [getStaffWithoutSBAccounts, { loading: loadingStaffWithoutSBAccounts, data: staffWithoutSBAccounts }] = useLazyQuery(GET_STAFF_WITHOUT_SB_ACCOUNTS, {
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      if (data?.staffWithoutSBAccounts?.length > 0 && allowManualSbAccountMatching) {
        setShowOpenToStaffModal(true)
      } else {
        setForm(prev => ({ ...prev, openStaff: true }))
      }
    }
  })  

  const [getStudentsWithoutGuardians, { loading: loadingStudentsWithoutGuardians, data: orphanedStudents }] = useLazyQuery(GET_STUDENTS_WITHOUT_GUARDIANS, {
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      if (data?.studentsWithoutGuardians?.length > 0) {
        setShowOpenToParentsModal(true)
      } else {
        setForm(prev => ({ ...prev, openParents: true }))
      }
    }
  })  
  
  /**
   * Functions
   */
  const handleOpenToParents = () => {
    setForm({...form, openParents: true})
    setShowOpenToParentsModal(false)
  }

  const handleOpenToStaff = () => {
    setForm({...form, openStaff: true})
    setShowOpenToStaffModal(false)
  }

  const handleOpenToParentsCheck = (isSelected) => {
    if (isSelected) {
      getStudentsWithoutGuardians({
        variables: {
          input: {
            event_id: event.id,
          }
        }
      })
    } else {
      setForm({...form, openParents: isSelected})
    }
  }

  const handleOpenToStaffCheck = (isSelected) => {
    if (isSelected) {
      getStaffWithoutSBAccounts({
        variables: {
          input: {
            event_id: event.id,
          }
        }
      })
    } else {
      setForm({...form, openStaff: isSelected})
    }
  }

  const handleSave = () => {
    updateEvent({ 
      variables: {
        input: {
          id: event.id,
          open_staff: form.openStaff,
          open_parents: form.openParents,
          disable_parent_bookings: form.disableParentBookings,
        }
      },
      
      onCompleted: (response) => {
        notify({ title: "Success", type: "success", content: `Event updated successfully.`, open: true })
        setCurrentEvent(event.id) // forces user context to refresh event data
        client.refetchQueries({ include: "active" }); // refetch all active queries to reflect the new session
        setSavedFormData({ ...form })
      },

      onError: (error) => {
        notify({ title: "Error", type: "danger", content: `An error occurred while updating the event.`, open: true })
      }
    })
  }

  const isDirty = JSON.stringify(form) !== JSON.stringify(savedFormData)
  const isReadOnlyMode = hasExpired(mySqlToLocal(event.end_date))
  
  return (
    <div>
      <div className="flex justify-end">
        {isReadOnlyMode && <ReadOnlyBadge className="mb-4" isFullWidth size="lg" />}
      </div>
      
      <div className="mb-6">
        <CustomSwitch
          title="Open to staff"
          description="Allow staff to view the event."
          isSelected={form.openStaff}
          isLoading={loadingStaffWithoutSBAccounts}
          handleChange={(isSelected) => handleOpenToStaffCheck(isSelected)}
          isDisabled={isReadOnlyMode}
        />

        <Divider className="my-3" />

        <CustomSwitch
          title="Open to parents"
          description="Allow parents to view the event."
          isSelected={form.openParents}
          handleChange={(isSelected) => handleOpenToParentsCheck(isSelected)}
          isLoading={loadingStudentsWithoutGuardians}
          isDisabled={isReadOnlyMode}
        />

        <Divider className="my-3" />
        
        <CustomSwitch
          title="Disable Bookings"
          description="Disable parent bookings for this event."
          isSelected={form.disableParentBookings}
          handleChange={(isSelected) => setForm({...form, disableParentBookings: isSelected})}
          isDisabled={isReadOnlyMode}
        />
      </div>

      <Button className="font-semibold" color="secondary" isDisabled={!isDirty} fullWidth onPress={() => handleSave()} isLoading={savingEvent}>Save</Button>
      {backButton}

      {showOpenToParentsModal && (
        <OpenToParentsModal 
          show 
          handleOpenToParents={handleOpenToParents} 
          handleCancel={() => setShowOpenToParentsModal(false)} 
          orphanedStudents={orphanedStudents?.studentsWithoutGuardians || []}
        />
      )}

      {showOpenToStaffModal && (
        <OpenToStaffModal 
          show
          handleOpenToStaff={handleOpenToStaff}
          handleCancel={() => setShowOpenToStaffModal(false)}
          staffWithoutSBAccounts={staffWithoutSBAccounts?.staffWithoutSBAccounts || []} 
        />
      )}
    </div>

  )
}

EventAccess.propTypes = {
  allowManualSbAccountMatching: PropTypes.bool,
  backButton: PropTypes.element.isRequired,
  event: PropTypes.shape({
    id: PropTypes.string.isRequired,
    open_staff: PropTypes.bool.isRequired,
    open_parents: PropTypes.bool.isRequired,
    disable_parent_bookings: PropTypes.bool.isRequired,
  }).isRequired,
}

export default EventAccess